import {
  PriceEvolutionData,
  PriceEvolutionByCategory,
  Insight,
  PriceMode,
} from './PriceEvolutionChart.types'

/**
 * Convert price based on price mode for rentals
 * API stores monthly prices, so yearly mode multiplies by 12
 */
export const convertRentalPrice = (
  price: number,
  isRental: boolean,
  priceMode?: PriceMode
): number => {
  if (!isRental || !priceMode || priceMode === 'monthly') {
    return price
  }
  return price * 12
}

export const formatPrice = (
  price: number,
  isRental: boolean = false,
  isSqm: boolean = false,
  priceMode?: PriceMode
): string => {
  if (isNaN(price) || price === 0) return 'CHF 0'

  // Convert price based on mode for rentals
  const displayPrice = convertRentalPrice(price, isRental, priceMode)

  // Use 2 decimals for rental m² prices (typically CHF 15-35, where decimals matter)
  // Use 0 decimals for all other prices
  const useDecimals = isRental && isSqm
  const fractionDigits = useDecimals ? 2 : 0

  const formatter = new Intl.NumberFormat('en-CH', {
    style: 'currency',
    currency: 'CHF',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })

  const formatted = useDecimals
    ? formatter.format(displayPrice)
    : formatter.format(Math.round(displayPrice))

  if (isRental) {
    const periodSuffix = priceMode === 'yearly' ? '/y' : '/m'
    if (isSqm) {
      return `${formatted}/m²${periodSuffix}`
    }
    return `${formatted}${periodSuffix}`
  }
  if (isSqm) {
    return `${formatted}/m²`
  }

  return formatted
}

export const formatShortPrice = (
  value: number,
  isRental: boolean = false,
  isSqm: boolean = false,
  priceMode?: PriceMode
): string => {
  // Convert price based on mode for rentals
  const displayValue = convertRentalPrice(value, isRental, priceMode)

  // Use decimals for rental m² prices (small values where decimals matter)
  const useDecimals = isRental && isSqm

  if (displayValue >= 1000000) {
    const millions = displayValue / 1000000
    return `${millions.toFixed(millions >= 10 ? 0 : 1)}M`
  } else if (displayValue >= 1000) {
    const thousands = displayValue / 1000
    return `${thousands.toFixed(thousands >= 10 ? 0 : 1)}k`
  } else {
    return useDecimals ? displayValue.toFixed(2) : displayValue.toFixed(0)
  }
}

export const formatQuarter = (dateString: string): string => {
  const date = new Date(dateString)
  const month = date.getMonth()
  const year = date.getFullYear()
  const quarter = Math.floor(month / 3) + 1
  return `Q${quarter} ${year}`
}

export const formatPropertyCategory = (category: string): string => {
  return category
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

// Minimum sample size to include a quarter (filters out statistically unreliable data)
const MIN_SAMPLE_SIZE = 10

// Helper to round price values based on whether decimals should be preserved
const roundPrice = (value: number, preserveDecimals: boolean): number => {
  if (preserveDecimals) {
    // Round to 2 decimal places for rental m² prices
    return Math.round(value * 100) / 100
  }
  return Math.round(value)
}

// Convert an ISO date string to a continuous quarter index (year * 4 + quarter).
// Lets the smoother reason about real elapsed time instead of array position,
// so quarters that are absent from the data set don't get implicitly skipped.
const dateToQuarterIndex = (dateString: string): number => {
  const d = new Date(dateString)
  return d.getFullYear() * 4 + Math.floor(d.getMonth() / 3)
}

// Apply weighted moving average smoothing.
// Window is time-aware: only includes prior points within `windowSize` quarters
// of the current one. Weight = windowSize - distance, so the current quarter
// gets the highest weight and points >= windowSize quarters away are excluded.
// On a gap-free series with windowSize=4 this matches the original [1,2,3,4]
// weighting; on a gappy series it avoids pulling 18-month-old data into the
// average.
const applyWeightedMovingAverage = (
  data: PriceEvolutionData[],
  windowSize: number = 4,
  preserveDecimals: boolean = false
): PriceEvolutionData[] => {
  if (data.length < 2) return data

  const indices = data.map((p) => dateToQuarterIndex(p.date))

  return data.map((point, index) => {
    const currentIdx = indices[index]
    const window: PriceEvolutionData[] = []
    const weights: number[] = []

    for (let j = 0; j <= index; j++) {
      const distance = currentIdx - indices[j]
      if (distance >= 0 && distance < windowSize) {
        window.push(data[j])
        weights.push(windowSize - distance)
      }
    }

    let totalWeight = 0
    let weightedMedian = 0
    let weightedAvg = 0
    let weightedCount = 0

    window.forEach((p, k) => {
      const w = weights[k]
      weightedMedian += p.medianPrice * w
      weightedAvg += p.avgPrice * w
      weightedCount += p.count * w
      totalWeight += w
    })

    const smoothed: PriceEvolutionData = {
      ...point,
      medianPrice: roundPrice(weightedMedian / totalWeight, preserveDecimals),
      avgPrice: roundPrice(weightedAvg / totalWeight, preserveDecimals),
      count: Math.round(weightedCount / totalWeight),
    }

    if (point.quantiles && window.every((p) => p.quantiles)) {
      const weightedQuantile = (key: 'q10' | 'q30' | 'q50' | 'q70' | 'q90') =>
        roundPrice(
          window.reduce(
            (s, p, k) => s + (p.quantiles?.[key] || 0) * weights[k],
            0
          ) / totalWeight,
          preserveDecimals
        )

      smoothed.quantiles = {
        q10: weightedQuantile('q10'),
        q30: weightedQuantile('q30'),
        q50: weightedQuantile('q50'),
        q70: weightedQuantile('q70'),
        q90: weightedQuantile('q90'),
      }
    }

    return smoothed
  })
}

// Drop quarters whose listing count is below the statistical-reliability threshold.
const filterByMinSampleSize = (
  data: PriceEvolutionData[],
  minCount: number = MIN_SAMPLE_SIZE
): PriceEvolutionData[] => {
  return data.filter((point) => point.count >= minCount)
}

export const processTimeSeriesData = (
  aggregations: any,
  preserveDecimals: boolean = false
): PriceEvolutionByCategory => {
  if (!aggregations?.price_over_time?.buckets) {
    return {}
  }

  const rawBuckets = aggregations.price_over_time.buckets
  const propertyCategories = new Set<string>()
  const timeSeriesData: PriceEvolutionByCategory = {}
  const allTypesData: PriceEvolutionData[] = []

  // Check if buckets is an object (keyed by quarter like "2024-Q1") or array
  const isKeyedBuckets =
    rawBuckets && typeof rawBuckets === 'object' && !Array.isArray(rawBuckets)

  // Convert quarter key (e.g., "2024-Q1") to date string (e.g., "2024-01-01")
  const quarterKeyToDateString = (quarterKey: string): string => {
    const match = quarterKey.match(/^(\d{4})-Q(\d)$/)
    if (match) {
      const year = match[1]
      const quarter = parseInt(match[2])
      const month = String((quarter - 1) * 3 + 1).padStart(2, '0')
      return `${year}-${month}-01`
    }
    return quarterKey
  }

  // Normalize buckets to array format for processing
  let buckets: Array<{ key: string; data: any }>

  if (isKeyedBuckets) {
    // Convert keyed buckets to array and sort by quarter
    buckets = Object.entries(rawBuckets)
      .map(([key, data]) => ({ key, data }))
      .sort((a, b) => a.key.localeCompare(b.key))
  } else {
    // Already an array (legacy format)
    buckets = rawBuckets.map((bucket: any) => ({
      key: bucket.key_as_string,
      data: bucket,
    }))
  }

  // For legacy array format, calculate min/max dates for quarter completeness check
  let minDate: Date | null = null
  let maxDate: Date | null = null

  if (!isKeyedBuckets) {
    buckets.forEach(({ key }) => {
      const date = new Date(key)
      if (!minDate || date < minDate) minDate = date
      if (!maxDate || date > maxDate) maxDate = date
    })
  }

  const isQuarterComplete = (
    quarterDate: Date,
    minDate: Date,
    maxDate: Date
  ): boolean => {
    const quarterMonth = quarterDate.getMonth()
    const quarterYear = quarterDate.getFullYear()
    const quarterStart = new Date(quarterYear, quarterMonth, 1)
    const quarterEnd = new Date(quarterYear, quarterMonth + 3, 0)
    return quarterStart >= minDate && quarterEnd <= maxDate
  }

  // Collect all property categories
  buckets.forEach(({ data }) => {
    const bucketData = isKeyedBuckets ? data : data
    if (bucketData.by_property_category?.buckets) {
      bucketData.by_property_category.buckets.forEach((categoryBucket: any) => {
        propertyCategories.add(categoryBucket.key)
      })
    }
  })

  propertyCategories.forEach((category) => {
    timeSeriesData[category] = []
  })

  buckets.forEach(({ key, data }) => {
    const dateString = isKeyedBuckets ? quarterKeyToDateString(key) : key
    const bucketDate = new Date(dateString)
    const bucketData = isKeyedBuckets ? data : data

    // For legacy format, check quarter completeness
    if (!isKeyedBuckets) {
      if (
        !minDate ||
        !maxDate ||
        !isQuarterComplete(bucketDate, minDate, maxDate)
      ) {
        return
      }
    }

    let totalCount = 0

    propertyCategories.forEach((category) => {
      const categoryBucket = bucketData.by_property_category?.buckets?.find(
        (b: any) => b.key === category
      )

      if (categoryBucket?.median_price?.values) {
        const medianPrice = roundPrice(
          categoryBucket.median_price.values['50.0'] || 0,
          preserveDecimals
        )
        const count = categoryBucket.listing_count?.value || 0

        const dataPoint: PriceEvolutionData = {
          date: dateString,
          avgPrice: roundPrice(
            categoryBucket.avg_price?.value || 0,
            preserveDecimals
          ),
          medianPrice,
          count,
        }

        // Add quantiles if available
        if (categoryBucket.quantiles?.values) {
          dataPoint.quantiles = {
            q10: roundPrice(
              categoryBucket.quantiles.values['10.0'] || 0,
              preserveDecimals
            ),
            q30: roundPrice(
              categoryBucket.quantiles.values['30.0'] || 0,
              preserveDecimals
            ),
            q50: roundPrice(
              categoryBucket.quantiles.values['50.0'] || 0,
              preserveDecimals
            ),
            q70: roundPrice(
              categoryBucket.quantiles.values['70.0'] || 0,
              preserveDecimals
            ),
            q90: roundPrice(
              categoryBucket.quantiles.values['90.0'] || 0,
              preserveDecimals
            ),
          }
        }

        timeSeriesData[category].push(dataPoint)
        totalCount += count
      }
    })

    if (bucketData.overall_median?.values && totalCount > 0) {
      const overallMedian = roundPrice(
        bucketData.overall_median.values['50.0'] || 0,
        preserveDecimals
      )

      const allTypesDataPoint: PriceEvolutionData = {
        date: dateString,
        avgPrice: roundPrice(
          bucketData.total_avg_price?.value || 0,
          preserveDecimals
        ),
        medianPrice: overallMedian,
        count: totalCount,
      }

      // Add overall quantiles if available
      if (bucketData.overall_quantiles?.values) {
        allTypesDataPoint.quantiles = {
          q10: roundPrice(
            bucketData.overall_quantiles.values['10.0'] || 0,
            preserveDecimals
          ),
          q30: roundPrice(
            bucketData.overall_quantiles.values['30.0'] || 0,
            preserveDecimals
          ),
          q50: roundPrice(
            bucketData.overall_quantiles.values['50.0'] || 0,
            preserveDecimals
          ),
          q70: roundPrice(
            bucketData.overall_quantiles.values['70.0'] || 0,
            preserveDecimals
          ),
          q90: roundPrice(
            bucketData.overall_quantiles.values['90.0'] || 0,
            preserveDecimals
          ),
        }
      }

      allTypesData.push(allTypesDataPoint)
    }
  })

  const minDataPoints = 2
  const significantCategories = Object.entries(timeSeriesData)
    .filter(([_category, data]) => {
      return data.length >= minDataPoints
    })
    .reduce((acc, [category, data]) => {
      // Pipeline:
      // 1. Drop quarters below the minimum sample size (statistically unreliable).
      // 2. Apply weighted moving average (smooths quarter-to-quarter variation).
      const filtered = filterByMinSampleSize(data)
      const smoothed = applyWeightedMovingAverage(filtered, 4, preserveDecimals)

      if (smoothed.length >= minDataPoints) {
        acc[category] = smoothed
      }
      return acc
    }, {} as PriceEvolutionByCategory)

  if (allTypesData.length >= minDataPoints) {
    const filteredAllTypes = filterByMinSampleSize(allTypesData)
    const smoothedAllTypes = applyWeightedMovingAverage(
      filteredAllTypes,
      4,
      preserveDecimals
    )

    if (smoothedAllTypes.length >= minDataPoints) {
      significantCategories['All Types'] = smoothedAllTypes
    }
  }

  return significantCategories
}

export const calculateKPIs = (
  data: PriceEvolutionByCategory,
  priceType: 'median' | 'average' = 'median'
) => {
  const allPrices: number[] = []
  const latestPrices: Record<string, number> = {}
  const firstPrices: Record<string, number> = {}
  let totalVolume = 0

  Object.entries(data).forEach(([category, values]) => {
    if (values.length > 0 && category !== 'All Types') {
      const priceField = priceType === 'median' ? 'medianPrice' : 'avgPrice'
      latestPrices[category] = values[values.length - 1][priceField]
      firstPrices[category] = values[0][priceField]
      values.forEach((v) => {
        allPrices.push(v[priceField])
        totalVolume += v.count
      })
    }
  })

  const priceField = priceType === 'median' ? 'medianPrice' : 'avgPrice'
  const avgPrice =
    data['All Types'] && data['All Types'].length > 0
      ? data['All Types'][data['All Types'].length - 1][priceField]
      : allPrices.length > 0
        ? allPrices.reduce((a, b) => a + b, 0) / allPrices.length
        : 0

  const mostExpensiveCategory = Object.entries(latestPrices)
    .filter(([category]) => category !== 'All Types')
    .sort((a, b) => b[1] - a[1])[0]

  let overallGrowth = 0
  if (data['All Types'] && data['All Types'].length > 0) {
    const allTypesData = data['All Types']
    const latest = allTypesData[allTypesData.length - 1]
    const oneYearAgoIndex = Math.max(0, allTypesData.length - 5)
    const oneYearAgoData = allTypesData[oneYearAgoIndex]

    if (oneYearAgoData && oneYearAgoData[priceField] > 0) {
      const oneYearAgoPrice = oneYearAgoData[priceField]
      const latestPrice = latest[priceField]
      overallGrowth = ((latestPrice - oneYearAgoPrice) / oneYearAgoPrice) * 100
    }
  } else {
    const growthRates: number[] = []
    Object.entries(data).forEach(([category, values]) => {
      if (category !== 'All Types' && values.length >= 5) {
        const latest = values[values.length - 1]
        const oneYearAgoIndex = Math.max(0, values.length - 5)
        const oneYearAgo = values[oneYearAgoIndex]

        if (oneYearAgo && oneYearAgo[priceField] > 0) {
          const growth =
            ((latest[priceField] - oneYearAgo[priceField]) /
              oneYearAgo[priceField]) *
            100
          growthRates.push(growth)
        }
      }
    })

    overallGrowth =
      growthRates.length > 0
        ? growthRates.reduce((a, b) => a + b, 0) / growthRates.length
        : 0
  }

  return {
    avgPrice,
    overallGrowth,
    mostExpensiveCategory,
    totalVolume,
  }
}

export const getChartColors = (): Record<string, string> => ({
  apartment: '#2563eb',
  house: '#10b981',
  commercial: '#f59e0b',
  plot: '#ef4444',
  'all types': '#8b5cf6',
})

type TranslationFn = (key: string, options?: Record<string, unknown>) => string

const defaultT: TranslationFn = (key: string) => {
  const parts = key.split('.')
  return parts[parts.length - 1] || key
}

export const generateInsights = (
  data: PriceEvolutionByCategory,
  priceType: 'median' | 'average' = 'median',
  t: TranslationFn = defaultT
): Insight[] => {
  const insights: Insight[] = []

  if (Object.keys(data).length === 0) return insights

  const categories = Object.keys(data)
  const priceField = priceType === 'median' ? 'medianPrice' : 'avgPrice'

  const allTypesData = data['All Types']
  if (allTypesData && allTypesData.length >= 2) {
    const firstQuarter = allTypesData[0]
    const lastQuarter = allTypesData[allTypesData.length - 1]
    const overallChange =
      ((lastQuarter[priceField] - firstQuarter[priceField]) /
        firstQuarter[priceField]) *
      100

    insights.push({
      title: t('charts.insights.marketTrend'),
      description: t(
        'charts.insights.descriptions.priceEvolution.marketTrend',
        {
          direction:
            overallChange >= 0
              ? t('charts.common.increased') || 'increased'
              : t('charts.common.decreased') || 'decreased',
          change: Math.abs(overallChange).toFixed(1),
          priceType:
            priceType === 'median'
              ? t('charts.priceTypes.median') || 'median'
              : t('charts.priceTypes.average') || 'average',
          startPrice: formatPrice(firstQuarter[priceField]),
          endPrice: formatPrice(lastQuarter[priceField]),
        }
      ),
    })
  }

  const categoryGrowth = categories
    .filter((cat) => cat !== 'All Types' && data[cat].length >= 2)
    .map((category) => {
      const catData = data[category]
      const firstPrice = catData[0][priceField]
      const lastPrice = catData[catData.length - 1][priceField]
      const growth = ((lastPrice - firstPrice) / firstPrice) * 100
      return { category, growth }
    })
    .sort((a, b) => b.growth - a.growth)

  if (categoryGrowth.length > 0) {
    const best = categoryGrowth[0]
    insights.push({
      title: t('charts.insights.bestPerformer'),
      description: t(
        'charts.insights.descriptions.priceEvolution.bestPerformer',
        {
          category: formatPropertyCategory(best.category),
          growth: best.growth.toFixed(1),
        }
      ),
    })
  }

  const allVolatilities = categories
    .map((category) => {
      const catData = data[category]
      if (catData.length < 3) return null

      const changes = []
      for (let i = 1; i < catData.length; i++) {
        const change = Math.abs(
          ((catData[i][priceField] - catData[i - 1][priceField]) /
            catData[i - 1][priceField]) *
            100
        )
        changes.push(change)
      }

      const avgVolatility = changes.reduce((a, b) => a + b, 0) / changes.length
      return { category, volatility: avgVolatility }
    })
    .filter(Boolean) as Array<{ category: string; volatility: number }>

  if (allVolatilities.length > 0) {
    const mostStable = allVolatilities.reduce((min, curr) =>
      curr.volatility < min.volatility ? curr : min
    )

    insights.push({
      title: t('charts.insights.marketStability'),
      description: t(
        'charts.insights.descriptions.priceEvolution.marketStability',
        {
          category: formatPropertyCategory(mostStable.category),
          volatility: mostStable.volatility.toFixed(1),
        }
      ),
    })
  }

  if (allTypesData && allTypesData.length >= 5) {
    const recentData = allTypesData.slice(-4)
    const recentChange =
      ((recentData[3][priceField] - recentData[0][priceField]) /
        recentData[0][priceField]) *
      100
    const momentum =
      recentChange > 2
        ? t('charts.common.strongUpward') || 'strong upward'
        : recentChange < -2
          ? t('charts.common.downward') || 'downward'
          : t('charts.common.stable') || 'stable'

    insights.push({
      title: t('charts.insights.recentMomentum'),
      description: t(
        'charts.insights.descriptions.priceEvolution.recentMomentum',
        {
          momentum,
          change: Math.abs(recentChange).toFixed(1),
          direction:
            recentChange >= 0
              ? t('charts.common.increase') || 'increase'
              : t('charts.common.decrease') || 'decrease',
          priceType,
        }
      ),
    })
  }

  return insights
}
